import { Line } from "@/js/interfaces/DTOs/Models";

export default class LineBag {
    lines: Array<Line>;

    constructor(lines: Array<Line> = []) {
        this.lines = lines;
    }


    /**
     * Get all lines in the collection
     *
     * @returns An array of lines
     */
    public all(): Array<Line> {
        return this.lines;
    }

    /**
     * Get a line from the collection using its index in the array
     * @param lineIndex The index of the line to get in the array
     * @returns The line at the specified index in the collection
     */
    public getFromIndex(lineIndex: number): Line {
        if (lineIndex < 0 || lineIndex >= this.lines.length) {
            throw new Error(`Index ${lineIndex} out of bounds`);
        }
        return this.lines[lineIndex];
    }

    /**
     * Get a line from the collection using its ID
     * @param lineId The ID of the line to get
     * @returns The line with the specified ID in the collection
     */
    public getFromId(lineId: number): Line {
        return this.getFromIndex(
            this.getIndexFromId(lineId),
        );
    }

    /**
     * Update a given price field for a given line.
     *
     * @param lineIndex The index of the line to update the price for in the array
     * @param property The price property to update, can be "supply", "drop" or "drop_off"
     * @param value The new value to set for the specified price property
     */
    public updatePrice(lineIndex: number, property: "supply" | "drop" | "drop_off", value: number): void {
        const line = this.getFromIndex(lineIndex);
        if (!(property in line)) {
            throw new Error(`Property ${property} does not exist on line`);
        }
        line[property] = value;
    }

    /**
     * Get the number of lines in the collection
     *
     * @returns The number of lines in the collection
     */
    public count(): number {
        return this.lines.length;
    }

    /**
     * Add a line to the collection
     *
     * @param line The line to add
     */
    public add(line: Line): void {
        const order = line.order;
        this.lines.splice(order, 0, line);
        this.updateInternalOrder();
    }

    /**
     * Delete a line from the collection
     *
     * @param line The line to delete
     * @return The ID of the deleted line
     */
    public delete(line: Line): number {
        const index = this.lines.indexOf(line);
        if (-1 === index) {
            throw new Error(`Line ${line.id} not found in collection`);
        }
        this.lines.splice(index, 1);
        this.updateInternalOrder();

        return line.id;
    }

    /**
     * Delete a line from the collection using its index in the array
     *
     * @param index The index of the line to delete in the array
     * @return The ID of the deleted line
     */
    public deleteFromIndex(index: number): number {
        if (index < 0 || index >= this.lines.length) {
            throw new Error(`Index ${index} out of bounds`);
        }
        return this.delete(this.lines[index]);
    }

    /**
     * Checks if there is no line.
     */
    public isEmpty(): boolean {
        return 0 === this.lines.length;
    }

    /**
     * Checks if there is at least one line.
     */
    public isNotEmpty(): boolean {
        return !this.isEmpty();
    }

    /**
     * Soft delete a line from the collection, it will be kept in the collection but marked as not in next version, and optionally as deleted in next version if it is soft deleted in next version.
     *
     * @param lineIndex The line index to soft delete
     * @param ifSoftDeletedInNextVersion If soft deleted, delete it in next version
     */
    public softDelete(lineIndex: number, ifSoftDeletedInNextVersion: boolean = false): void {
        if (lineIndex < 0 || lineIndex >= this.lines.length) {
            throw new Error(`Index ${lineIndex} out of bounds`);
        }
        const line = this.lines[lineIndex];
        line.not_in_next_version = true;
        if (ifSoftDeletedInNextVersion) {
            line.delete_in_next_version = true;
        }
    }

    /**
     * Unsoft delete/restore a line from the collection, it will be kept in the collection but marked as in next version and not deleted in next version.
     *
     * @param lineIndex The line index to restore
     */
    public restore(lineIndex: number): void {
        if (lineIndex < 0 || lineIndex >= this.lines.length) {
            throw new Error(`Index ${lineIndex} out of bounds`);
        }
        const line = this.lines[lineIndex];
        line.not_in_next_version = false;
        line.delete_in_next_version = false;
    }

    /**
     * Replace a line in the collection at a specific index with a new line
     * @param line The line to replace in the collection, identified by its index in the array
     * @param index The index of the line to replace in the array
     */
    public replace(index: number, newLine: Line): void {
        if (index < 0 || index >= this.lines.length) {
            throw new Error(`Index ${index} out of bounds`);
        }
        this.lines.splice(index, 1, newLine);
    }

    /**
     * Replace multiple lines in the collection at specific indices with new lines
     * @param lines The lines to replace in the collection, identified by their indices in the array
     * @param page The page number to calculate the base index
     * @param linePerPage The number of lines per page to calculate the base index
     */
    public replaceManyPaginated(lines: Array<Line>, page: number, linePerPage: number): void {
        const baseIndex = LineBag.getBaseIndexFromPage(page, linePerPage);
        lines.forEach((line, index) => {
            const lineIndex = baseIndex + index;
            if (lineIndex < 0 || lineIndex >= this.lines.length) {
                throw new Error(`Index ${lineIndex} out of bounds`);
            }
            this.lines.splice(lineIndex, 1, line);
        });
    }

    /**
     * Move a line from an old index to a new index in the collection
     * @param lineId The ID of the line to move
     * @param newIndex The new index for the line in the collection
     */
    public moveLine(lineId: number, newIndex: number): void {
        const oldIndex = this.getIndexFromId(lineId);
        if (null === oldIndex) {
            throw new Error(`Line ID ${lineId} not found in collection`);
        }
        if (newIndex < 0 || newIndex >= this.lines.length) {
            throw new Error(`New index ${newIndex} out of bounds`);
        }

        const line = this.lines[oldIndex];
        if (line.id !== lineId) {
            throw new Error(`Line ID at old index ${oldIndex} does not match the provided line ID ${lineId}`);
        }
        this.lines.splice(oldIndex, 1);
        this.lines.splice(newIndex, 0, line);

        // Update the order property of the lines
        this.lines.forEach((curLine, index) => {
            if (curLine.id !== lineId) {
                curLine.order = index;
            } else {
                curLine.order = newIndex;
            }
        });
    }

    /**
     * Update the order property of each line in the collection to match its index in the array.
     * This is useful to call after adding, deleting or moving lines to keep the order property up to date with the actual order of lines in the collection.
     */
    public updateInternalOrder(): void {
        this.lines.forEach((curLine, index) => {
            curLine.order = index;
        });
    }

    /**
     * Get the index of a line in the collection using the line object
     * @param line The line to get the index from
     * @returns The index of the line in the collection
     */
    public getIndexFromLine(line: Line): number {
        const index = this.lines.indexOf(line);
        if (-1 === index) {
            throw new Error(`Line ${line.id} not found in collection`);
        }
        return index;
    }

    /**
     * Check if a line with a given ID exists in the collection
     * @param lineId The ID of the line to check for existence
     * @returns true if a line with the given ID exists in the collection, false otherwise
     */
    public hasIndexFromId(lineId: number): boolean {
        return -1 !== this.lines.findIndex(line => line.id === lineId);
    }

    /**
     * Get the index of a line in the collection using its ID
     * @param lineId The ID of the line to get the index from
     * @returns The index of the line in the collection
     */
    public getIndexFromId(lineId: number): number {
        const lineIndex = this.lines.findIndex(line => line.id === lineId);
        if (-1 === lineIndex) {
            throw new Error(`Line ID ${lineId} not found in collection`);
        }
        return lineIndex;
    }

    /**
     * Get the ID of a line in the collection using its index in the array
     * @param lineIndex The index of the line to get the ID from
     * @returns The ID of the line at the specified index in the collection
     */
    public getIdFromIndex(lineIndex: number): number {
        if (lineIndex < 0 || lineIndex >= this.lines.length) {
            throw new Error(`Index ${lineIndex} out of bounds`);
        }
        return this.lines[lineIndex].id;
    }

    /**
     * Check if a line should be soft deleted, meaning it is not marked as not in next version, it can be soft deleted if it is not marked as not in next version.
     *
     * @param lineIndex The line index to check
     * @returns true if the line should be soft deleted, false otherwise
     */
    public shouldSoftDelete(lineIndex: number): boolean {
        return !this.lines[lineIndex].not_in_next_version;
    }

    /**
     * Check if a line is existing before the current version, meaning it is not a new line added in this version, it can be soft deleted if it is existing before current version.
     *
     * @param lineIndex The line index to check
     * @returns true if the line is existing before the current version, false otherwise
     */
    public isExistingBeforeCurrentVersion(lineIndex: number): boolean {
        return !this.lines[lineIndex].add_in_this_version;
    }

    /**
     * Mark a line as updated, meaning it has new or dirty line or remark, this is used to know if we need to refresh the line in the UI after an update.
     *
     * @param lineIndex The line index to mark as updated
     */
    public setLineAsDirty(lineIndex: number): void {
        this.lines[lineIndex].has_new_or_dirty_line_or_remark = true;
    }

    /**
     * Mark a line as updated using its ID, this is used to know if we need to refresh the line in the UI after an update.
     *
     * @param lineId The line ID to mark as updated
     */
    public setLineIdAsDirty(lineId: number): void {
        const lineIndex = this.getIndexFromId(lineId);
        this.setLineAsDirty(lineIndex);
    }

    /**
     * Set the document id and name for a line, this is used after attaching a document to a line to update the line in the collection with the new document information.
     *
     * @param lineIndex The line index to set the document for
     * @param documentId The document id to set for the line
     * @param filename The document name to set for the line
     */
    public setDocument(lineIndex: number, documentId: number, filename: string): void {
        this.lines[lineIndex].document_id = documentId;
        this.lines[lineIndex].document_name = filename;
    }

    /**
     * Remove the document id and name for a line, this is used after dissociating a document from a line to update the line in the collection by removing the document information.
     *
     * @param lineIndex The line index to remove the document from
     */
    public removeDocument(lineIndex: number): void {
        this.lines[lineIndex].document_id = null;
        this.lines[lineIndex].document_name = null;
    }

    /**
     * Fill the collection with fake lines, used to display skeletons while loading real lines
     *
     * @param quantity The quantity of fake lines to create
     */
    public static fake(quantity: number): LineBag {
        return new this(
            Array(quantity).fill({ not_loaded: true }),
        );
    }

    /**
     * Get the base index for a page, used to calculate the line index in the collection from the line index in the page
     *
     * @param page The page number to get the base index for
     * @param linePerPage The number of lines per page to get the base index for
     * @returns The base index for the page
     */
    private static getBaseIndexFromPage(page: number, linePerPage: number): number {
        return (page - 1) * linePerPage;
    }
}
